import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, PieChart, Pie, AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, Radar } from 'recharts';
import { X, BarChart3, Target, TrendingUp, Brain, BookOpen, Activity, Crosshair, ArrowRight, Check, ChevronRight } from 'lucide-react';

// ============ MOCK 3-MONTH DATA ============

const MOCK_SCORING = [
  { week: 'W1', score: 88, putts: 34, gir: 6 },
  { week: 'W2', score: 91, putts: 36, gir: 5 },
  { week: 'W3', score: 86, putts: 32, gir: 8 },
  { week: 'W4', score: 84, putts: 31, gir: 9 },
  { week: 'W5', score: 87, putts: 33, gir: 7 },
  { week: 'W6', score: 83, putts: 30, gir: 10 },
  { week: 'W7', score: 85, putts: 32, gir: 8 },
  { week: 'W8', score: 82, putts: 29, gir: 11 },
  { week: 'W9', score: 81, putts: 30, gir: 10 },
  { week: 'W10', score: 84, putts: 31, gir: 9 },
  { week: 'W11', score: 80, putts: 28, gir: 12 },
  { week: 'W12', score: 79, putts: 28, gir: 11 },
];

const MOCK_SG = [
  { round: 'R1', sg_total: -4.2, sg_putting: -1.1, sg_ttg: -3.1 },
  { round: 'R2', sg_total: -3.8, sg_putting: -0.5, sg_ttg: -3.3 },
  { round: 'R3', sg_total: -2.1, sg_putting: 0.3, sg_ttg: -2.4 },
  { round: 'R4', sg_total: -2.5, sg_putting: -0.2, sg_ttg: -2.3 },
  { round: 'R5', sg_total: -1.8, sg_putting: 0.5, sg_ttg: -2.3 },
  { round: 'R6', sg_total: -1.2, sg_putting: 0.8, sg_ttg: -2.0 },
  { round: 'R7', sg_total: -1.5, sg_putting: 0.2, sg_ttg: -1.7 },
  { round: 'R8', sg_total: -0.8, sg_putting: 0.6, sg_ttg: -1.4 },
];

const MOCK_MENTAL = [
  { week: 'W1', focus: 5, confidence: 4, control: 4, commitment: 6, enjoyment: 5 },
  { week: 'W4', focus: 6, confidence: 5, control: 5, commitment: 7, enjoyment: 6 },
  { week: 'W8', focus: 7, confidence: 7, control: 6, commitment: 8, enjoyment: 7 },
  { week: 'W12', focus: 8, confidence: 8, control: 7, commitment: 9, enjoyment: 8 },
];

const MOCK_RADAR_BEFORE = [
  { skill: 'Focus', value: 5 }, { skill: 'Confidence', value: 4 },
  { skill: 'Emotional Control', value: 4 }, { skill: 'Commitment', value: 6 },
  { skill: 'Routine', value: 3 }, { skill: 'Enjoyment', value: 5 },
];
const MOCK_RADAR_AFTER = [
  { skill: 'Focus', value: 8 }, { skill: 'Confidence', value: 8 },
  { skill: 'Emotional Control', value: 7 }, { skill: 'Commitment', value: 9 },
  { skill: 'Routine', value: 7 }, { skill: 'Enjoyment', value: 8 },
];

const MOCK_SESSION_TYPES = [
  { name: 'Practice', value: 24, color: '#2C5530' },
  { name: 'Tournament', value: 12, color: '#BC562B' },
  { name: 'Coaching', value: 8, color: '#233C5A' },
];

const MOCK_CLUB_COMPARISON = [
  { club: 'Driver', saved: 280, actual: 273 },
  { club: '3 Wood', saved: 245, actual: 241 },
  { club: '5 Iron', saved: 180, actual: 176 },
  { club: '7 Iron', saved: 160, actual: 158 },
  { club: '9 Iron', saved: 140, actual: 137 },
  { club: 'PW', saved: 130, actual: 128 },
  { club: 'SW', saved: 100, actual: 96 },
];

const MOCK_SG_HOLES = Array.from({ length: 18 }, (_, i) => ({
  hole: i + 1,
  sg: +(Math.random() * 2 - 0.8).toFixed(2),
}));

// ============ REPORT MODALS ============

const REPORTS = [
  {
    id: 'scoring',
    title: 'Scoring Trends',
    subtitle: 'Track your scoring improvement over time',
    icon: TrendingUp,
    color: '#2C5530',
    description: 'See how your average score, putts per round, and greens in regulation evolve week by week. Identify trends and set realistic improvement targets.',
    chart: () => (
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={MOCK_SCORING} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F5F0EB" />
          <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#57534E' }} />
          <YAxis domain={[75, 95]} tick={{ fontSize: 11, fill: '#57534E' }} />
          <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }} />
          <Line type="monotone" dataKey="score" stroke="#2C5530" strokeWidth={2.5} dot={{ r: 3 }} name="Score" />
          <Line type="monotone" dataKey="putts" stroke="#233C5A" strokeWidth={2} dot={{ r: 3 }} name="Putts" />
        </LineChart>
      </ResponsiveContainer>
    ),
  },
  {
    id: 'strokes-gained',
    title: 'Strokes Gained Analysis',
    subtitle: 'Compare your game against PGA Tour & Scratch benchmarks',
    icon: BarChart3,
    color: '#BC562B',
    description: 'The most powerful metric in modern golf. See exactly where you\'re gaining and losing strokes — Total, Putting, and Tee-to-Green — tracked across every round.',
    chart: () => (
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={MOCK_SG} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F5F0EB" vertical={false} />
          <XAxis dataKey="round" tick={{ fontSize: 11, fill: '#57534E' }} />
          <YAxis tick={{ fontSize: 11, fill: '#57534E' }} />
          <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }} />
          <Bar dataKey="sg_total" name="SG Total" fill="#2C5530" radius={[3, 3, 0, 0]} />
          <Bar dataKey="sg_putting" name="SG Putting" fill="#233C5A" radius={[3, 3, 0, 0]} />
          <Bar dataKey="sg_ttg" name="SG Tee-to-Green" fill="#BC562B" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    ),
  },
  {
    id: 'sg-per-hole',
    title: 'Per-Hole SG Breakdown',
    subtitle: 'Pinpoint exactly which holes cost you strokes',
    icon: Target,
    color: '#233C5A',
    description: 'After every round, see a hole-by-hole Strokes Gained breakdown. Instantly identify the holes where you gained the most and lost the most — then focus your practice accordingly.',
    chart: () => (
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={MOCK_SG_HOLES} margin={{ top: 10, right: 5, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F5F0EB" vertical={false} />
          <XAxis dataKey="hole" tick={{ fontSize: 10, fill: '#57534E' }} />
          <YAxis tick={{ fontSize: 11, fill: '#57534E' }} />
          <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }} />
          <Bar dataKey="sg" name="SG" radius={[3, 3, 0, 0]} maxBarSize={24}>
            {MOCK_SG_HOLES.map((entry, idx) => (
              <Cell key={idx} fill={entry.sg >= 0 ? '#2C5530' : '#BC562B'} opacity={0.85} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    ),
  },
  {
    id: 'mental-scorecard',
    title: 'Mental Performance Radar',
    subtitle: 'Visualise your mental game growth',
    icon: Brain,
    color: '#6B5B73',
    description: 'Rate yourself across Focus, Confidence, Emotional Control, Commitment, Routine, and Enjoyment after each round. Watch your mental skills radar expand over 3 months of structured coaching.',
    chart: () => (
      <div className="flex items-center justify-center gap-4">
        <div className="text-center">
          <p className="text-xs font-bold text-[#BC562B] mb-2">Week 1</p>
          <ResponsiveContainer width={220} height={200}>
            <RadarChart data={MOCK_RADAR_BEFORE}>
              <PolarGrid stroke="#E7E5E4" />
              <PolarAngleAxis dataKey="skill" tick={{ fontSize: 9, fill: '#57534E' }} />
              <Radar dataKey="value" stroke="#BC562B" fill="#BC562B" fillOpacity={0.2} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <div className="text-center">
          <p className="text-xs font-bold text-[#2C5530] mb-2">Week 12</p>
          <ResponsiveContainer width={220} height={200}>
            <RadarChart data={MOCK_RADAR_AFTER}>
              <PolarGrid stroke="#E7E5E4" />
              <PolarAngleAxis dataKey="skill" tick={{ fontSize: 9, fill: '#57534E' }} />
              <Radar dataKey="value" stroke="#2C5530" fill="#2C5530" fillOpacity={0.2} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
    ),
  },
  {
    id: 'session-mix',
    title: 'Session Activity Breakdown',
    subtitle: 'Understand how you spend your golf time',
    icon: Activity,
    color: '#2C5530',
    description: 'Track the balance between practice, tournament play, and coaching sessions. A healthy mix across all three leads to the fastest improvement. See your activity trends over 3 months.',
    chart: () => (
      <div className="flex items-center justify-center gap-8">
        <ResponsiveContainer width={200} height={200}>
          <PieChart>
            <Pie data={MOCK_SESSION_TYPES} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
              {MOCK_SESSION_TYPES.map((entry, idx) => (
                <Cell key={idx} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="space-y-3">
          {MOCK_SESSION_TYPES.map(s => (
            <div key={s.name} className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
              <div>
                <p className="text-sm font-semibold text-[#1A1A1A]">{s.name}</p>
                <p className="text-xs text-[#57534E]">{s.value} sessions</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 'club-distances',
    title: 'Club Distance Tracking',
    subtitle: 'Know your real carry distances vs what you think',
    icon: Crosshair,
    color: '#37474F',
    description: 'Record your expected yardages for every club, then compare against actual distances from practice sessions. Many golfers overestimate their distances by 5-15 yards — this feature reveals the truth.',
    chart: () => (
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={MOCK_CLUB_COMPARISON} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F5F0EB" vertical={false} />
          <XAxis dataKey="club" tick={{ fontSize: 10, fill: '#57534E' }} />
          <YAxis tick={{ fontSize: 11, fill: '#57534E' }} />
          <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }} />
          <Bar dataKey="saved" name="Saved Yardage" fill="#233C5A" radius={[3, 3, 0, 0]} />
          <Bar dataKey="actual" name="Actual Avg" fill="#2C5530" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    ),
  },
  {
    id: 'mental-trends',
    title: 'Mental Performance Trends',
    subtitle: 'Track your mental skills week by week',
    icon: BookOpen,
    color: '#5F8A7B',
    description: 'Watch your Focus, Confidence, Emotional Control, and Commitment scores climb over a 3-month coaching programme. Evidence-based proof that mental game training works.',
    chart: () => (
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={MOCK_MENTAL} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F5F0EB" />
          <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#57534E' }} />
          <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: '#57534E' }} />
          <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }} />
          <Area type="monotone" dataKey="focus" name="Focus" stroke="#2C5530" fill="#2C5530" fillOpacity={0.1} strokeWidth={2} />
          <Area type="monotone" dataKey="confidence" name="Confidence" stroke="#BC562B" fill="#BC562B" fillOpacity={0.1} strokeWidth={2} />
          <Area type="monotone" dataKey="control" name="Control" stroke="#233C5A" fill="#233C5A" fillOpacity={0.1} strokeWidth={2} />
          <Area type="monotone" dataKey="commitment" name="Commitment" stroke="#6B5B73" fill="#6B5B73" fillOpacity={0.1} strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    ),
  },
];

// ============ MODAL COMPONENT ============

function ReportModal({ report, onClose }) {
  if (!report) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="report-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E7E5E4] flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: report.color + '15' }}>
              <report.icon className="w-5 h-5" style={{ color: report.color }} />
            </div>
            <div>
              <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A]">{report.title}</h3>
              <p className="text-xs text-[#57534E]">{report.subtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[#A8A29E] hover:text-[#1A1A1A] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-sm text-[#57534E] leading-relaxed mb-6">{report.description}</p>
          <div className="bg-[#F9F7F4] rounded-xl p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#A8A29E] mb-3">Sample 3-Month Data</p>
            {report.chart()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ MAIN PAGE ============

export default function FeaturesShowcasePage() {
  const [activeReport, setActiveReport] = useState(null);

  return (
    <div className="min-h-screen bg-white" data-testid="features-showcase-page">
      {/* Hero */}
      <div className="bg-[#233C5A] text-white py-16 sm:py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-white/50 mb-4">Quiet Mind Golf</p>
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-5xl mb-5 leading-tight text-white">
            Your Complete Mental<br />Performance Coaching Portal
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed mb-8">
            Data-driven tools and structured coaching to transform your mental game. Track every round, measure what matters, and build unshakeable confidence on the course.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="px-6 py-3 bg-white text-[#233C5A] font-semibold rounded-lg hover:bg-[#F5F0EB] transition-colors" data-testid="cta-register">
              Start Free
            </Link>
            <a href="#reports" className="px-6 py-3 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors" data-testid="cta-see-reports">
              See the Reports
            </a>
          </div>
        </div>
      </div>

      {/* Key Benefits */}
      <div className="py-16 px-6 bg-[#F9F7F4]">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#1A1A1A] text-center mb-4">Why Golfers Choose Quiet Mind</h2>
          <p className="text-sm text-[#57534E] text-center max-w-xl mx-auto mb-12">Built by a PGA Professional and Mental Performance Coach with real tour experience</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Strokes Gained Analysis', desc: 'Know exactly where you\'re losing strokes — not guessing. Compare against PGA Tour and scratch benchmarks.', icon: BarChart3 },
              { title: 'QMG Blueprint', desc: 'A proven Awareness-Acceptance-Action framework with guided breathing, body scans, and AI pressure scenarios.', icon: Brain },
              { title: 'Live Round Scoring', desc: 'Record stats hole-by-hole on your phone during play. Score, putts, fairways, GIR, clubs, wind — everything.', icon: Target },
              { title: 'Coach-Connected', desc: 'Your coach sees your data in real-time. Data-driven feedback replaces guesswork and recall bias.', icon: Activity },
            ].map(b => (
              <div key={b.title} className="bg-white rounded-xl p-6 border border-[#E7E5E4]">
                <b.icon className="w-7 h-7 text-[#2C5530] mb-4" />
                <h3 className="text-sm font-bold text-[#1A1A1A] mb-2">{b.title}</h3>
                <p className="text-xs text-[#57534E] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 3-Month Journey */}
      <div className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#1A1A1A] mb-3">What 3 Months Looks Like</h2>
          <p className="text-sm text-[#57534E]">Real improvement, measured and visible. Here's what a typical golfer achieves.</p>
        </div>
        <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { stat: '-7', label: 'Average Score Drop', sub: '88 to 81' },
            { stat: '+4.1', label: 'SG Total Improvement', sub: 'vs Scratch baseline' },
            { stat: '83%', label: 'Mental Score Increase', sub: 'Focus & Confidence' },
            { stat: '44', label: 'Sessions Logged', sub: 'Practice + Rounds' },
          ].map(s => (
            <div key={s.label} className="bg-[#F9F7F4] rounded-xl p-6">
              <p className="text-3xl sm:text-4xl font-bold text-[#2C5530]">{s.stat}</p>
              <p className="text-sm font-semibold text-[#1A1A1A] mt-2">{s.label}</p>
              <p className="text-xs text-[#A8A29E] mt-1">{s.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Interactive Report Previews */}
      <div className="py-16 px-6 bg-[#233C5A]" id="reports">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-white text-center mb-3">Interactive Report Previews</h2>
          <p className="text-sm text-white/60 text-center max-w-xl mx-auto mb-10">Click any report to see a live preview with sample 3-month data</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {REPORTS.map(report => (
              <button
                key={report.id}
                onClick={() => setActiveReport(report)}
                className="bg-white/5 border border-white/10 rounded-xl p-5 text-left hover:bg-white/10 hover:border-white/20 transition-all group"
                data-testid={`report-preview-${report.id}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/10">
                    <report.icon className="w-5 h-5 text-white/80" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/30 ml-auto group-hover:text-white/60 transition-colors" />
                </div>
                <h3 className="text-sm font-bold text-white mb-1">{report.title}</h3>
                <p className="text-xs text-white/50 leading-relaxed">{report.subtitle}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Features List */}
      <div className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#1A1A1A] text-center mb-10">Everything You Get</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
            {[
              'Live Round Scoring (hole-by-hole)',
              'Strokes Gained vs PGA Tour & Scratch',
              'Per-hole SG breakdown charts',
              'Session logging (Practice/Tournament/Coach)',
              'Shotgun Pattern analysis & trends',
              'Club distance tracking (Saved vs Actual)',
              'Performance Scorecards (6 mental pillars)',
              'QMG in Motion breathing & body scans',
              'AI-powered pressure scenario practice',
              'Personal diary & reflections',
              'Coach-connected data sharing',
              'Reports & trend visualisations',
              'Pillars of Performance learning',
              'Direct messaging with your coach',
              'Mobile-optimised for on-course use',
              'Secure cloud-based data storage',
            ].map(feature => (
              <div key={feature} className="flex items-center gap-3 py-2">
                <Check className="w-4 h-4 text-[#2C5530] flex-shrink-0" />
                <span className="text-sm text-[#1A1A1A]">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-16 px-6 bg-[#2C5530] text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-white mb-4">Ready to Transform Your Mental Game?</h2>
          <p className="text-sm text-white/70 mb-8 leading-relaxed">Join golfers who are using data and structured mental coaching to play better, score lower, and enjoy the game more.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="px-6 py-3 bg-white text-[#2C5530] font-semibold rounded-lg hover:bg-[#F5F0EB] transition-colors" data-testid="cta-register-bottom">
              Start Free Today
            </Link>
            <Link to="/booking" className="px-6 py-3 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors" data-testid="cta-booking">
              Book a Session
            </Link>
          </div>
        </div>
      </div>

      {/* Report Modal */}
      <ReportModal report={activeReport} onClose={() => setActiveReport(null)} />
    </div>
  );
}
